import {
  addPickingFlowBranches,
  deactivatePickingFlowBranches,
  searchActivePickingFlowBranches,
  searchInactivePickingFlowBranches,
} from 'api/mdl11'
import { searchCompanyName } from 'Utils/functions'

const PAGE_SIZE = 200

const getCompanyName = branch => {
  if (!branch?.CompanyId) return 'N/A'
  return searchCompanyName(branch)
}

const normalizeBranch = (branch, Active) => {
  const normalizedBranch = {
    ...branch,
    CompanyId: branch.CompanyId?.toUpperCase(),
  }

  return {
    ...normalizedBranch,
    Name: branch.Description?.trim() || '',
    CompanyName: getCompanyName(normalizedBranch),
    Active,
  }
}

const getResponseData = response => {
  if (response?.status !== 200 || response?.data?.Status?.Code !== 200) {
    throw new Error(response?.data?.Status?.Message || 'Invalid API response')
  }

  return response.data.Data || []
}

const fetchAllPages = async (searchService, Active) => {
  const branches = []
  let pageNumber = 1
  let totalRows = 0
  let hasMoreResults = true

  while (hasMoreResults) {
    const response = await searchService({
      search: '',
      pageNumber,
      pageSize: PAGE_SIZE,
    })
    const page = getResponseData(response)

    branches.push(...page.map(branch => normalizeBranch(branch, Active)))
    totalRows = Number(page[0]?.TotalRows) || branches.length
    pageNumber += 1
    hasMoreResults = page.length > 0 && branches.length < totalRows
  }

  return branches
}

const groupBranchesByCompany = branches =>
  branches.reduce((groups, branch) => {
    if (!groups[branch.CompanyId]) groups[branch.CompanyId] = []
    groups[branch.CompanyId].push({
      branchOfficeId: branch.BranchOfficeId,
    })
    return groups
  }, {})

const persistBranchesByCompany = (service, branches) =>
  Object.entries(groupBranchesByCompany(branches)).map(
    ([companyId, companyBranches]) =>
      service({
        companyId,
        branches: companyBranches,
      })
  )

const validatePersistenceResponse = response => {
  getResponseData(response)
}

const waitForPersistenceRequests = async requests => {
  const results = await Promise.all(
    requests.map(request =>
      request.then(
        response => ({ response }),
        error => ({ error })
      )
    )
  )
  const failedRequest = results.find(result => result.error)

  if (failedRequest) throw failedRequest.error
  results.forEach(result => validatePersistenceResponse(result.response))
}

export const fetchPickingFlowBranches = () => async () => {
  const [activeBranches, inactiveBranches] = await Promise.all([
    fetchAllPages(searchActivePickingFlowBranches, true),
    fetchAllPages(searchInactivePickingFlowBranches, false),
  ])

  return [...activeBranches, ...inactiveBranches]
}

export const savePickingFlowBranches =
  (activeBranches, previouslyActiveBranches) => async () => {
    try {
      const activeIds = new Set(
        activeBranches.map(branch => branch.BranchOfficeId)
      )
      const previousIds = new Set(
        previouslyActiveBranches.map(branch => branch.BranchOfficeId)
      )
      const branchesToAdd = activeBranches.filter(
        branch => !previousIds.has(branch.BranchOfficeId)
      )
      const branchesToDeactivate = previouslyActiveBranches.filter(
        branch => !activeIds.has(branch.BranchOfficeId)
      )

      await waitForPersistenceRequests([
        ...persistBranchesByCompany(addPickingFlowBranches, branchesToAdd),
        ...persistBranchesByCompany(
          deactivatePickingFlowBranches,
          branchesToDeactivate
        ),
      ])
      return true
    } catch (error) {
      console.error('Error saving picking flow branches:', error)
      return false
    }
  }
